import { Component, EventEmitter, Inject, OnDestroy, OnInit } from '@angular/core';
import Utils from '../../../util';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { take, takeUntil } from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {GetNameDialogComponent} from '../../results-helper/get-name.dialog';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-result-list-dialog',
  templateUrl: './result-list-dialog.component.html',
  styleUrls: ['./result-list-dialog.component.scss']
})
export class ResultListDialogComponent extends UserpilotReloader implements OnInit, OnDestroy {
  onDestroyNotifier = new EventEmitter();

  listData = [];
  currentSelection: string;
  Utils = Utils;
  trigger: string;

  constructor(
    private projectCommService: ProjectCommunicationService,
    public dialogRef: MatDialogRef<ResultListDialogComponent>,
    private translationService: AnessaTranslationService,
    @Inject(MAT_DIALOG_DATA) public data: ResultDialogData,
    public dialogService: MatDialog
  ) {
    super();
  }

  ngOnInit() {
    this.listData = this.projectCommService.getResultNames();
    this.currentSelection = this.projectCommService.getCurrentResultsName();
    this.sortListData();
  }

  sortListData() {
    if (this.listData) {
      this.listData.sort((a, b) => {
        return b.timestamp - a.timestamp;
      });
    }
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.trigger = language;
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  canEditPlant() {
    return Utils.canEditProject();
  }

  onClick(set: { starred: boolean, timestamp: number }) {
    this.projectCommService.selectResult(set.timestamp);
    this.data.selectionMade = true;
    this.dialogRef.close(this.data);
  }

  onClickLatest() {
    this.projectCommService.selectResult('latest');
    this.data.selectionMade = true;
    this.dialogRef.close(this.data);
  }

  getDateInFormat(timestamp: number) {
    return this.projectCommService.timestampToDateString(timestamp);
  }

  toggleFavourite(set: any) {
    this.projectCommService.markResultFavourite(set.timestamp, !set.starred);
    set.starred = !set.starred;
  }

  renameResult(resultsSet) {
    if (!this.canEditPlant()) { return; }
    const name = resultsSet.name ? resultsSet.name : this.getDateInFormat(resultsSet.timestamp);
    const dialogRef = this.dialogService.open(GetNameDialogComponent, {
      width: 'max-content',
      data: {name: name, instruction: 'SERVICE.PROJECT_COMM.INPUT_RESULT_NAME'}
    });
    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.saveClicked && result.name) {
        this.projectCommService.promiseRenameResult(resultsSet.timestamp, result.name)
          .then(resultNames => {
            this.listData = resultNames as any[];
            this.sortListData();
          });
      }
    });
  }

  onCancelClick() {
    this.dialogRef.close(this.data);
  }

  isSelected(set: any) {
    return set.timestamp + '' === this.currentSelection;
  }

  onExportClick(set: any) {
    this.projectCommService.promiseResultsSet(set['timestamp']).then(results => {
      const resultsJson = new Blob([JSON.stringify(results)], { type: 'text/json' });
      const url = window.URL.createObjectURL(resultsJson);
      const link = document.createElement('a');
      const projName = this.projectCommService.getProjectInfo().title;
      const timeStamp = this.projectCommService.timestampToDateString(results['timestamp']).replace(':', '-');
      link.download = `${projName} - results - ${timeStamp}.json`;
      link.href = url;
      link.click();
    });
  }

  getLatestSet() {
    return this.listData.reduce((previous, current) =>
      (previous['timestamp'] > current['timestamp']) ? previous : current
    );
  }

  isFirstRow(index: number, rowData: any) {
    return index === 0;
  }

}

export interface ResultDialogData {
  selectionMade: boolean;
}
